import API from 'util/API';

const DEFAULT_COUNTRIES = ['au', 'my', 'nz', 'sg'];

/**
 * Generate a session token for grouped autocomplete + details billing.
 * Use the same token for every keystroke of a single search session,
 * then send it again with the matching details call.
 * @returns {string}
 */
export const createPlacesSessionToken = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

/**
 * Fetch autocomplete predictions from the backend proxy.
 * @param {Object} params
 * @param {string} params.input - User input text.
 * @param {string} params.sessionToken - Session token (see createPlacesSessionToken).
 * @param {string[]} [params.countries] - ISO 3166-1 alpha-2 country codes.
 * @param {string} [params.language] - BCP-47 language tag.
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal] - Optional abort signal for cancellation.
 * @returns {Promise<Array<{ placeId: string, description: string, mainText: string, secondaryText: string }>>}
 */
export const fetchPlaceAutocomplete = async (
  { input, sessionToken, countries = DEFAULT_COUNTRIES, language = 'en' },
  { signal } = {},
) => {
  if (!input || !input.trim()) {
    return [];
  }

  const response = await API.get('/places/autocomplete', {
    params: {
      input,
      sessionToken,
      countries: countries.join(','),
      language,
    },
    signal,
  });

  const predictions = response?.data?.predictions;
  return Array.isArray(predictions) ? predictions : [];
};

/**
 * Fetch place details (address components + geometry) from the backend proxy.
 * @param {Object} params
 * @param {string} params.placeId - Google place_id from a prediction.
 * @param {string} params.sessionToken - Same session token used for autocomplete.
 * @param {string} [params.language] - BCP-47 language tag.
 * @returns {Promise<{ addressComponents: Array, geometry: Object | null, formattedAddress: string }>}
 */
export const fetchPlaceDetails = async ({ placeId, sessionToken, language = 'en' }) => {
  if (!placeId) {
    throw new Error('placeId is required');
  }

  const response = await API.get('/places/details', {
    params: {
      placeId,
      sessionToken,
      language,
    },
  });

  const result = response?.data?.result;
  return {
    addressComponents: result?.address_components || [],
    geometry: result?.geometry || null,
    formattedAddress: result?.formatted_address || '',
  };
};
